import React from 'react';
import { useSelector } from 'react-redux';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  getMetricMetaByKey,
  getMetricUnitText,
  LESS_THAN_NUMBER,
  TOOLTIP_METRICS,
} from '@utils/metrics';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { Trend } from '@components/common';
import { humanize } from '@utils';
import { realTimeModeOnTrendsSelector } from '@store/common/selectors';
import Toolbox from './Toolbox';
import DistributionToggle from './DistributionToggle';
import { useDodData } from '../hooks/useDodSeries';
import { useMetricData } from '../hooks/useMetricSeries';
import BooleanFilter from './Tools/BooleanFilter';
import { useLiveSummaryData } from '../hooks/useLiveSummary';
import { DATA_TAGS } from '../hooks/useData';
import {
  Subscript,
  HeaderWrapper,
  ValueWrapper,
  TitleWrapper,
  LiveModeMarker,
  IconWrap,
  SecondRow,
} from './styled';
import { getDodDiff } from '@src/components/metrics-panel-hz/utils/calculateDodTotal';

// import CompareFilterTotals from './CompareFilterTotals';

function Header({
  metric,
  chartMetric,
  showTrend,
  chartMode,
  setChartMode,
  toggleLegend,
  distribution,
  selectDistribution,
  naMode,
}) {
  // to show totals and diff
  const { total: metricTotal } = useMetricData({ metric: chartMetric, dataTag: DATA_TAGS.primary });
  const { total: dodTotal } = useDodData({ metric: chartMetric, dataTag: DATA_TAGS.primaryDod });
  const isLiveModeSelected = useSelector(realTimeModeOnTrendsSelector);
  const isTooltipNeeded = TOOLTIP_METRICS[CURRENT_PRODUCT_MODE].includes(metric);
  const trend = getDodDiff(metricTotal, dodTotal);

  let value =
    metricTotal < LESS_THAN_NUMBER && metricTotal > 0
      ? '<'.concat(LESS_THAN_NUMBER)
      : humanize(metricTotal);

  // total value for live mode has it's own query
  const liveSummaryData = useLiveSummaryData({ metric: chartMetric });
  if (isLiveModeSelected) {
    value = humanize(liveSummaryData.total);
  }

  const metricInfo = getMetricMetaByKey({ metricKey: chartMetric });
  const units = getMetricUnitText(metricInfo.unit);
  const metricValue = value && `${value}${units} `;
  const subTitle = `${metricInfo.short} ${metricInfo.subscript}`;

  return (
    <HeaderWrapper>
      <TitleWrapper>
        <h4 style={{ marginBottom: 0 }}>
          {metricInfo.displayName}
          {isTooltipNeeded && (
            <IconWrap>
              <Tooltip title={metricInfo.helpText}>
                <InfoCircleOutlined />
              </Tooltip>
            </IconWrap>
          )}
          <Subscript title={subTitle}>{metricInfo.subscript}</Subscript>
        </h4>
      </TitleWrapper>

      <SecondRow>
        {naMode ? (
          <h4 className="na-text">Not Applicable</h4>
        ) : (
          <ValueWrapper>
            {isLiveModeSelected && metricValue && <LiveModeMarker />}
            {metricValue}
            {showTrend && (
              <Trend
                value={trend}
                style={{ textAlign: 'left', paddingLeft: 4 }}
                reverse={metricInfo.reverse}
              />
            )}
            <BooleanFilter metric={metric} />
            {/* <CompareFilterTotals chartMetric={chartMetric} metricInfo={metricInfo} /> */}
          </ValueWrapper>
        )}

        <DistributionToggle
          chartMode={chartMode}
          metric={metric}
          distribution={distribution}
          selectDistribution={selectDistribution}
        />
        <Toolbox
          metric={metric}
          chartMode={chartMode}
          setChartMode={setChartMode}
          toggleLegend={toggleLegend}
        />
      </SecondRow>
    </HeaderWrapper>
  );
}

export default Header;
